/**
 * Extracts the parent (top-level) category from various category formats.
 *
 * Handles:
 * - Coveo format: Array like ['3D', 'Characters'] → '3D'
 * - API format: String like '3D/Characters' or 'Tools' → '3D' or 'Tools'
 *
 * Note: Does NOT lowercase the result. The component handles case normalization
 * for icon/microcopy lookups, preserving the display-ready value for fallback.
 *
 * @param category - Category in array or string format
 * @returns Parent category (preserving original case), or undefined if not available
 */
export function getParentCategory(
  category: string | string[] | undefined | null
): string | undefined {
  if (!category) return undefined;

  // Handle array format (Coveo)
  if (Array.isArray(category)) {
    return category[0] || undefined;
  }

  // Handle string format (API) - split on common delimiters and take first segment
  // The regex handles both spaced (' > ') and non-spaced ('>') separator formats
  const segments = category.split(/[/>]/);
  const firstSegment = segments[0]?.trim();

  return firstSegment || undefined;
}

/**
 * Formats a category slug into a human-readable display name.
 *
 * Rules:
 * - Short slugs (2-3 chars) like "vfx", "ai" → uppercase "VFX", "AI"
 * - Slugs with hyphens like "add-ons" → "Add-Ons"
 * - Other slugs → Title Case
 *
 * @param slug - Category slug to format
 * @returns Formatted display name
 */
export function formatCategorySlug(slug: string): string {
  const lowerSlug = slug.toLowerCase();

  // Short slugs (1-3 chars) without hyphens are treated as acronyms (e.g., "vfx" → "VFX")
  // Hyphenated short slugs like "a-b" are excluded to use title-case instead
  if (lowerSlug.length <= 3 && !lowerSlug.includes('-')) {
    return lowerSlug.toUpperCase();
  }

  return lowerSlug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
}
